/**
 * Brand-wise Top Sale values in a dedicated Firestore collection.
 * A month keeps the last saved snapshot until a newer month is saved.
 */
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { MONTHLY_AUTOS_BRANDS } from "./monthlyAutosReportService";

export const COL_MONTHLY_TOP_SALES = "monthly_top_sales";

export function monthlyTopSalesDocId(year, monthIndex, branchFilter = "Main") {
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number(monthIndex)));
  const branch = String(branchFilter || "Main");
  return `${y}-${mi}-${branch}`;
}

export function emptyTopSalesValues() {
  const out = {};
  for (const b of MONTHLY_AUTOS_BRANDS) out[b] = "";
  return out;
}

function parseAmount(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(String(raw).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : null;
}

export function normalizeTopSalesValues(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const out = {};
  for (const b of MONTHLY_AUTOS_BRANDS) {
    const n = parseAmount(src[b]);
    if (n != null) out[b] = n;
  }
  return out;
}

function hasAnyValue(values) {
  return Object.values(values || {}).some((v) => v != null && v !== "" && Number.isFinite(Number(v)));
}

export async function loadMonthlyTopSalesDoc(year, monthIndex, branchFilter = "Main") {
  const docId = monthlyTopSalesDocId(year, monthIndex, branchFilter);
  const snap = await getDoc(doc(db, COL_MONTHLY_TOP_SALES, docId));
  if (!snap.exists()) return null;
  const d = snap.data() || {};
  const values = normalizeTopSalesValues(d.values);
  if (!hasAnyValue(values)) return null;
  return {
    id: snap.id,
    year: Number(d.year) || Number(year),
    monthIndex: Number.isFinite(Number(d.monthIndex)) ? Number(d.monthIndex) : Number(monthIndex),
    branchFilter: String(d.branchFilter || branchFilter),
    values,
    updatedAt: d.updatedAt ?? null,
    updatedBy: String(d.updatedBy || ""),
  };
}

/**
 * Resolve Top Sale for a month: use this month's saved doc, else the latest earlier month.
 */
export async function resolveMonthlyTopSales(
  year,
  monthIndex,
  branchFilter = "Main",
  lookbackMonths = 120
) {
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number(monthIndex)));
  const empty = {
    values: {},
    sourceYear: null,
    sourceMonthIndex: null,
    carried: false,
    found: false,
  };

  const BATCH = 12;
  for (let batchStart = 0; batchStart <= lookbackMonths; batchStart += BATCH) {
    const tasks = [];
    for (let k = 0; k < BATCH && batchStart + k <= lookbackMonths; k += 1) {
      const i = batchStart + k;
      const d = new Date(y, mi - i, 1);
      tasks.push(
        loadMonthlyTopSalesDoc(d.getFullYear(), d.getMonth(), branchFilter).then((docData) => ({
          i,
          calendarYear: d.getFullYear(),
          monthIndex: d.getMonth(),
          docData,
        }))
      );
    }
    const results = await Promise.all(tasks);
    results.sort((a, b) => a.i - b.i);
    for (const row of results) {
      if (!row.docData) continue;
      return {
        values: row.docData.values,
        sourceYear: row.calendarYear,
        sourceMonthIndex: row.monthIndex,
        carried: row.i > 0,
        found: true,
        updatedAt: row.docData.updatedAt,
        updatedBy: row.docData.updatedBy,
      };
    }
  }

  try {
    const { loadLatestTopSales } = await import("./monthlySaleReportManualService");
    const legacy = await loadLatestTopSales(y, mi, lookbackMonths, branchFilter);
    const values = normalizeTopSalesValues(legacy);
    if (hasAnyValue(values)) {
      return {
        values,
        sourceYear: null,
        sourceMonthIndex: null,
        carried: true,
        found: true,
        legacy: true,
      };
    }
  } catch {
    /* ignore */
  }

  return empty;
}

export async function saveMonthlyTopSales({
  year,
  monthIndex,
  branchFilter = "Main",
  values,
  updatedBy = "",
}) {
  const y = Number(year);
  const mi = Math.max(0, Math.min(11, Number(monthIndex)));
  const normalized = normalizeTopSalesValues(values);
  if (!hasAnyValue(normalized)) {
    throw new Error("Enter at least one Top Sale amount");
  }
  const docId = monthlyTopSalesDocId(y, mi, branchFilter);
  const payload = {
    year: y,
    monthIndex: mi,
    branchFilter: String(branchFilter || "Main"),
    values: normalized,
    updatedAt: serverTimestamp(),
    updatedBy: String(updatedBy || ""),
  };
  await setDoc(doc(db, COL_MONTHLY_TOP_SALES, docId), payload, { merge: true });
  return { docId, ...payload, values: normalized };
}

/** Overlay resolved Top Sale onto report.brands[].topSale */
export function applyTopSalesToReport(report, values) {
  if (!report?.brands?.length) return report;
  const map = normalizeTopSalesValues(values);
  for (const b of report.brands) {
    const n = map[b.name];
    b.topSale = n != null ? n : 0;
  }
  return report;
}
